/**
 * Canonical metadata and helper functions for WooCommerce Order Statuses.
 * Covers the 7 core statuses + 13 custom statuses configured on the store (20 total).
 *
 * Rules:
 * 1. Sales Stats (S21): strictly counts 'completed' and 'en-cours-de-prepa'. All other 18 statuses are excluded.
 * 2. Shop Manager COD (SM): all 20 statuses are supported, categorized into the confirmation pipeline,
 *    and filterable in the call center worklist.
 */
export const WOO_ORDER_STATUSES = [
    {
        slug: 'modification-conf',
        name: 'Modification Confirmée',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'confirmed',
        description: 'Modification Confirmée',
        badgeTone: 'signal',
        badgeLabel: 'Modif. confirmée',
    },
    {
        slug: 'en-cours-de-prepa',
        name: 'En cours de preparation',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: true, // COUNTED IN STATS
        defaultConfirmationStatus: 'confirmed',
        description: 'En cours de préparation',
        badgeTone: 'good',
        badgeLabel: 'En prépa',
    },
    {
        slug: 'on-hold',
        name: 'En attente',
        type: 'core',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        badgeTone: 'neutral',
        badgeLabel: 'En attente',
    },
    {
        slug: 'cancelled',
        name: 'Annulée',
        type: 'core',
        isPaid: false,
        isReports: false,
        countedInStats: false,
        defaultConfirmationStatus: 'cancelled',
        badgeTone: 'alarm',
        badgeLabel: 'Annulée',
    },
    {
        slug: 'kjbs',
        name: 'KJBS',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        description: 'Kef, Jendouba, Beja, Seliana',
        badgeTone: 'signal',
        badgeLabel: 'KJBS',
    },
    {
        slug: 'ready-1',
        name: 'Ready 1',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        badgeTone: 'signal',
        badgeLabel: 'Ready 1',
    },
    {
        slug: 'mod',
        name: 'Modification',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        description: 'Modifcation commande',
        badgeTone: 'signal',
        badgeLabel: 'Modification',
    },
    {
        slug: 'pending',
        name: 'Attente paiement',
        type: 'core',
        isPaid: false,
        isReports: false,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        badgeTone: 'neutral',
        badgeLabel: 'Attente paiement',
    },
    {
        slug: 'processing',
        name: 'En cours',
        type: 'core',
        isPaid: true,
        isReports: true,
        countedInStats: true, // COUNTED IN STATS (live online orders)
        defaultConfirmationStatus: 'pending_call',
        badgeTone: 'neutral',
        badgeLabel: 'En cours',
    },
    {
        slug: 'ready',
        name: 'Selon disponibilité',
        type: 'custom',
        isPaid: true,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'pending_call',
        description: 'Sélon disponibilité',
        badgeTone: 'signal',
        badgeLabel: 'Selon dispo',
    },
    {
        slug: 'completed',
        name: 'Terminée',
        type: 'core',
        isPaid: true,
        isReports: true,
        countedInStats: true, // COUNTED IN STATS
        defaultConfirmationStatus: 'confirmed',
        badgeTone: 'good',
        badgeLabel: 'Terminée',
    },
    {
        slug: 'refunded',
        name: 'Remboursée',
        type: 'core',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'cancelled',
        badgeTone: 'alarm',
        badgeLabel: 'Remboursée',
    },
    {
        slug: 'failed',
        name: 'Échouée',
        type: 'core',
        isPaid: false,
        isReports: false,
        countedInStats: false,
        defaultConfirmationStatus: 'cancelled',
        badgeTone: 'alarm',
        badgeLabel: 'Échouée',
    },
    {
        slug: 'checkout-draft',
        name: 'Brouillon',
        type: 'core',
        isPaid: false,
        isReports: false,
        countedInStats: false,
        defaultConfirmationStatus: 'cancelled',
        badgeTone: 'neutral',
        badgeLabel: 'Brouillon',
    },
    {
        slug: 'client-injoignabl',
        name: 'Client injoignable 1',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'unreachable',
        badgeTone: 'alarm',
        badgeLabel: 'Injoignable 1',
    },
    {
        slug: 'client-injoigna-2',
        name: 'Client injoignable 2',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'unreachable',
        description: 'Client injoignable 2',
        badgeTone: 'alarm',
        badgeLabel: 'Injoignable 2',
    },
    {
        slug: 'client-injoigna-3',
        name: 'Client injoignable 3',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'unreachable',
        description: 'Client injoignable 3',
        badgeTone: 'alarm',
        badgeLabel: 'Injoignable 3',
    },
    {
        slug: 'tel-ferme',
        name: 'Tel Fermé',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'unreachable',
        description: 'Tel Fermé',
        badgeTone: 'alarm',
        badgeLabel: 'Tél fermé',
    },
    {
        slug: 'confirme',
        name: 'Confirmé',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'confirmed',
        description: 'Commande Confirmé',
        badgeTone: 'good',
        badgeLabel: 'Confirmé',
    },
    {
        slug: 'confirme-tunis',
        name: 'Confirmé Tunis',
        type: 'custom',
        isPaid: false,
        isReports: true,
        countedInStats: false,
        defaultConfirmationStatus: 'confirmed',
        description: 'Confirmé Tunis',
        badgeTone: 'good',
        badgeLabel: 'Confirmé Tunis',
    },
];
const BY_SLUG = new Map(WOO_ORDER_STATUSES.map((s) => [s.slug, s]));
/** Normalize a status string by stripping any 'wc-' prefix and trimming. */
export function normalizeWooStatusSlug(raw) {
    if (raw === null || raw === undefined)
        return '';
    return raw.trim().replace(/^wc-/, '').toLowerCase();
}
/** Retrieve the canonical definition for a status slug, or null if unknown. */
export function getWooOrderStatus(raw) {
    const slug = normalizeWooStatusSlug(raw);
    return BY_SLUG.get(slug) ?? null;
}
/** Check if a WooCommerce status is counted in Sales Stats (S21). Only completed & en-cours-de-prepa count. */
export function isCountedInSalesStats(raw) {
    const slug = normalizeWooStatusSlug(raw);
    return slug === 'completed' || slug === 'en-cours-de-prepa' || slug === 'processing';
}
/** Slugs that are counted in Sales Stats, including any prefixed variants for database queries. */
export const SALES_STATS_STATUS_SLUGS = [
    'completed',
    'en-cours-de-prepa',
    'processing',
    'wc-completed',
    'wc-en-cours-de-prepa',
    'wc-processing',
];
/**
 * Closed / non-callable WooCommerce statuses:
 * Orders with these statuses must NEVER appear in the active "À appeler" call queue
 * because they are already closed, delivered, or confirmed on the shop side.
 */
export const NON_CALLABLE_WOO_STATUSES = [
    'completed',
    'en-cours-de-prepa',
    'confirme',
    'confirme-tunis',
    'modification-conf',
    'cancelled',
    'refunded',
    'failed',
    'checkout-draft',
];
/**
 * Determine the initial confirmation pipeline status for a newly imported WooCommerce order.
 */
export function getInitialConfirmationStatus(raw) {
    const slug = normalizeWooStatusSlug(raw);
    const def = BY_SLUG.get(slug);
    if (slug === 'client-injoigna-3') {
        return { confirmationStatus: 'unreachable', callAttempts: 3, isConfirmed: false };
    }
    if (slug === 'client-injoigna-2') {
        return { confirmationStatus: 'unreachable', callAttempts: 2, isConfirmed: false };
    }
    if (slug === 'client-injoignabl' || slug === 'tel-ferme') {
        return { confirmationStatus: 'unreachable', callAttempts: 1, isConfirmed: false };
    }
    if (def !== undefined) {
        return {
            confirmationStatus: def.defaultConfirmationStatus,
            callAttempts: 0,
            isConfirmed: def.defaultConfirmationStatus === 'confirmed',
        };
    }
    // Fallback for unknown statuses
    return { confirmationStatus: 'pending_call', callAttempts: 0, isConfirmed: false };
}
//# sourceMappingURL=woo-order-statuses.js.map